@import "../scss/old";

:host([disabled="true"]) {
  pointer-events: none;
}

:host([expanded]) {
  flex: 1 1 auto;
  width: 100%;
}

:host {
  box-sizing: border-box;
  display: inline-block;
  width: fit-content;

  a, button {
    @include css-vars((
      // Container
      button-bg-color: css-var(comp-button-fill-primary-background-color-default),
      button-border-width: css-var(comp-button-fill-border-width),
      button-border-color: css-var(comp-button-fill-primary-background-color-default),
      button-outline: 0,
      button-outline-offset: css-var(focus-ring-offset),
      button-display: inline-flex,
      button-gap: css-var(comp-icon-with-text-gap),
      button-radius: css-var(comp-button-border-radius),
      button-padding-inline: css-var(comp-button-padding-base-left),
      button-padding-box: css-var(comp-button-padding-base-top),
      button-margin-top: 0,
      button-margin-right: 0,
      button-margin-bottom: 0,
      button-margin-left: 0,
      button-width: auto,
      button-opacity: 1,

      // We need to calculate the button minimum size in case the button has no text
      // and only has an icon. In this case the line-height of the button is ignored
      // and displays a little smaller than it should.
      button-min-size: calc(
        (css-var(comp-button-text-base-font-size) * css-var(comp-button-text-base-line-height)) +
        (2 * css-var(comp-button-fill-border-width)) +
        css-var(comp-button-padding-base-top) +
        css-var(comp-button-padding-base-bottom)),

      // Text
      button-text-color: css-var(comp-button-fill-primary-foreground-color),
      button-text-align: css-var(text-align-center),
      button-text-font: css-var(comp-button-text-base),
      button-text-font-size: css-var(comp-button-text-base-font-size),
      button-text-letter-spacing: css-var(comp-button-text-base-letter-spacing),
      button-text-case: css-var(comp-button-text-base-text-case),
      button-text-decoration: css-var(comp-button-text-base-text-decoration),

      // Text on dark
      button-text-color-dark: css-var(comp-button-text-dark-mode-color-default),
      button-text-color-dark-active: css-var(comp-button-text-dark-mode-active),
      button-text-color-dark-hover: css-var(comp-button-text-dark-mode-hover),

      // Interaction
      button-cursor: pointer,
      button-pointed-events: auto,

      // Motion
      button-transition: css-var(transition-quick),

      // Icon
      button-icon-color: css-var(icon-color, comp-button-fill-primary-foreground-color),
    ));

    background: css-var(button-bg-color);
    border: css-var(button-border-width) solid css-var(button-border-color);
    border-radius: css-var(button-radius);
    outline: css-var(button-outline);
    outline-offset: css-var(button-outline-offset);
    display: css-var(button-display);
    gap: css-var(button-gap);
    padding: css-var(button-padding-box) css-var(button-padding-inline);
    margin: css-var(button-margin-top) css-var(button-margin-right) css-var(button-margin-bottom) css-var(button-margin-left);
    width: css-var(button-width);
    min-width: css-var(button-min-size);
    min-height: css-var(button-min-size);
    opacity: css-var(button-opacity);

    color: css-var(button-text-color);
    font: css-var(button-text-font);
    letter-spacing: css-var(button-text-letter-spacing);
    text-align: css-var(button-text-align);
    text-decoration: css-var(button-text-decoration);
    text-transform: css-var(button-text-case);

    transition: css-var(button-transition);
    cursor: css-var(button-cursor);
    pointer-events: css-var(button-pointer-events);

    box-sizing: border-box;
    align-items: center;
    justify-content: center;
    text-wrap: nowrap;

    &:hover {
      @include css-vars((
        // Container
        button-bg-color: css-var(comp-button-fill-primary-background-color-hover),
        button-border-color: css-var(comp-button-fill-primary-background-color-hover),
      ));
    }

    &:active:not(:disabled,.disabled,.loader) {
      @include css-vars((
        // Container
        button-bg-color: css-var(comp-button-fill-primary-background-color-active),
        button-border-color: css-var(comp-button-fill-primary-background-color-active),
      ));

      box-shadow: none;
      transform: translateY(1px);
    }

    &:focus,
    &:focus-visible {
      @include css-vars((
        // Container
        button-outline: css-var(comp-focus-ring-default-border),
      ));
    }

    &.disabled,
    &:disabled,
    fieldset.disabled,
    fieldset:disabled & {
      @include css-vars((
        // Container
        button-opacity: css-var(comp-button-disabled),
        // Interaction
        button-cursor: not-allowed,
        button-pointed-events: none,
      ));
    }

    &.loader {
      @include css-vars((
        // Interaction
        button-cursor: wait,
        button-pointed-events: none,
      ));
    }

    &.attention {
      @include css-vars((
        // Container
        button-bg-color: css-var(comp-button-fill-attention-background-color-default),
        button-border-color: css-var(comp-button-fill-attention-background-color-default),

        // Text
        button-text-color: css-var(comp-button-fill-attention-foreground-color),
      ));

      &:hover {
        @include css-vars((
          // Container
          button-bg-color: css-var(comp-button-fill-attention-background-color-hover),
          button-border-color: css-var(comp-button-fill-attention-background-color-hover),
        ));
      }

      &:active:not(:disabled,.disabled,.loader) {
        @include css-vars((
          // Container
          button-bg-color: css-var(comp-button-fill-attention-background-color-active),
          button-border-color: css-var(comp-button-fill-attention-background-color-active),
        ));
      }
    }

    &.outline {
      @include css-vars((
        // Container
        button-bg-color: transparent,
        button-border-color: css-var(comp-button-outline-primary-border-color),
        button-border-width: css-var(comp-button-outline-border-width),

        // Text
        button-text-color: css-var(comp-button-outline-primary-foreground-color),

        // Icons
        button-icon-color: css-var(icon-color, comp-button-outline-primary-foreground-color),
      ));

      &:hover:not(:disabled) {
        @include css-vars((
          // Container
          button-bg-color: css-var(comp-button-outline-primary-background-color-hover),
        ));
      }

      &:active:not(:disabled,.disabled,.loader) {
        @include css-vars((
          // Container
          button-bg-color: css-var(comp-button-outline-primary-background-color-active),
        ));
        box-shadow: none;
      }

      &.attention {
        @include css-vars((
          // Container
          button-border-color: css-var(comp-button-outline-attention-border-color),

          // Text
          button-text-color: css-var(comp-button-outline-attention-foreground-color),

          // Icons
          button-icon-color: css-var(icon-color, comp-button-outline-attention-foreground-color),
        ));

        &:hover:not(:disabled) {
          @include css-vars((
            // Container
            button-bg-color: css-var(comp-button-outline-attention-background-color-hover),
          ));
        }

        &:active:not(:disabled,.disabled,.loader) {
          @include css-vars((
            // Container
            button-bg-color: css-var(comp-button-outline-attention-background-color-active),
          ));
        }
      }
    }

    &.text {
      @include css-vars((
        // Container
        button-bg-color: transparent,
        button-border-color: transparent,
        button-padding-inline: css-var(comp-button-icon-padding-left),
        button-padding-box: css-var(comp-button-icon-padding-top),

        // Text
        button-text-color: css-var(comp-button-text-primary-color-default),

        // Icons
        button-icon-color: css-var(icon-color, comp-button-text-primary-color-default),
      ));

      &:active:not(:disabled,.disabled,.loader) {
        @include css-vars((
          // Container
          button-bg-color: transparent,
          button-border-color: transparent,

          // Text
          button-text-color: css-var(comp-button-text-primary-color-active),
          button-text-decoration: css-var(comp-button-text-base-underlined-text-decoration),

          button-icon-color: css-var(icon-color, comp-button-text-primary-color-active),
        ));
        box-shadow: none;
      }

      &:hover:not(:disabled,.disabled) {
        @include css-vars((
          // Text
          button-text-color: css-var(comp-button-text-primary-color-hover),
          button-text-decoration: css-var(comp-button-text-base-underlined-text-decoration),

          // Icons
          button-icon-color: css-var(icon-color, comp-button-text-primary-color-hover),
        ));
      }

      &.attention {
        @include css-vars((
          // Text
          button-text-color: css-var(comp-button-text-attention-color-default),

          // Icons
          button-icon-color: css-var(icon-color, comp-button-text-attention-color-default),
        ));

        &:active:not(:disabled,.disabled,.loader) {
          @include css-vars((
            // Text
            button-text-color: css-var(comp-button-text-attention-color-active),

            // Icons
            button-icon-color: css-var(icon-color, comp-button-text-attention-color-active),
          ));
        }

        &:hover:not(:disabled,.disabled) {
          @include css-vars((
            // Container
            button-bg-color: transparent,
            button-border-color: transparent,

            // Text
            button-text-color: css-var(comp-button-text-attention-color-hover),

            // Icons
            button-icon-color: css-var(icon-color, comp-button-text-attention-color-hover),
          ));
        }
      }

      &.text-secondary {
        @include css-vars((
          // Text
          button-text-color: css-var(comp-button-text-secondary-color-default),

          // Icons
          button-icon-color: css-var(icon-color, comp-button-text-secondary-color-default),
        ));

        &:hover:not(:disabled,.disabled) {
          @include css-vars((
            // Text
            button-text-color: css-var(comp-button-text-secondary-color-hover),

            // Icons
            button-icon-color: css-var(icon-color, comp-button-text-secondary-color-hover),
          ));
        }

        &:active:not(:disabled,.disabled,.loader) {
          @include css-vars((
            // Text
            button-text-color: css-var(comp-button-text-secondary-color-active),

            // Icons
            button-icon-color: css-var(icon-color, comp-button-text-secondary-color-active),
          ));

          box-shadow: none;
        }
      }

      &.text-dark {
        @include css-vars((
          // Text
          button-text-color: css-var(comp-button-text-dark-mode-color-default),

          // Icons
          button-icon-color: css-var(icon-color, comp-button-text-dark-mode-color-default),
        ));

        &:hover:not(:disabled,.disabled) {
          @include css-vars((
            // Text
            button-text-color: css-var(comp-button-text-dark-mode-color-hover),

            // Icons
            button-icon-color: css-var(icon-color, comp-button-text-dark-mode-color-hover),
          ));
        }

        &:active:not(:disabled,.disabled,.loader) {
          @include css-vars((
            // Text
            button-text-color: css-var(comp-button-text-dark-mode-color-active),

            // Icons
            button-icon-color: css-var(icon-color, comp-button-text-dark-mode-color-active),
          ));

          box-shadow: none;
        }
      }
    }

    &.small {
      @include css-vars((
        // Container
        button-padding-inline: css-var(comp-button-padding-s-left),
        button-padding-box: css-var(comp-button-padding-s-top),

        // We need to calculate the button minimum size in case the button has no text
        // and only has an icon. In this case the line-height of the button is ignored
        // and displays a little smaller than it should.
        button-min-size: calc(
          (css-var(comp-button-text-s-font-size) * css-var(comp-button-text-base-line-height)) +
          (2 * css-var(comp-button-fill-border-width)) +
          css-var(comp-button-padding-s-top) +
          css-var(comp-button-padding-s-bottom)),

        // Text
        button-text-font: css-var(comp-button-text-s),
        button-text-font-size: css-var(comp-button-text-s-font-size),
      ));
    }

    &.large {
      @include css-vars((
        // Container
        button-padding-inline: css-var(comp-button-padding-l-left),
        button-padding-box: css-var(comp-button-padding-l-top),

        // We need to calculate the button minimum size in case the button has no text
        // and only has an icon. In this case the line-height of the button is ignored
        // and displays a little smaller than it should.
        button-min-size: calc(
          (css-var(comp-button-text-l-font-size) * css-var(comp-button-text-base-line-height)) +
          (2 * css-var(comp-button-fill-border-width)) +
          css-var(comp-button-padding-l-top) +
          css-var(comp-button-padding-l-bottom)),

        // Text
        button-text-font: css-var(comp-button-text-l),
        button-text-font-size: css-var(comp-button-text-l-font-size),
      ));
    }

    &.expanded {
      @include css-vars((
        // Container
        button-display: flex,
        button-width: 100%,
      ));
    }

    &.pull-top {
      @include css-vars((
        button-margin-top: calc(css-var(button-padding-box) * -1),
      ));
    }

    &.pull-right {
      @include css-vars((
        button-margin-right: calc(css-var(button-padding-inline) * -1),
      ));
    }

    &.pull-bottom {
      @include css-vars((
        button-margin-bottom: calc(css-var(button-padding-box) * -1),
      ));
    }

    &.pull-left {
      @include css-vars((
        button-margin-left: calc(css-var(button-padding-inline) * -1),
      ));
    }

    .loader {
      box-sizing: border-box;
      animation: css-var(animation-spin);
      background: transparent;
      border: 2px solid currentColor;
      border-right-color: transparent;
      border-radius: 100%;
      display: block;
      width: css-var(button-text-font-size);
      height: css-var(button-text-font-size);
      margin-block: 0.09375rem;
    }

    .icon {
      --yth-comp-icon-color: var(--yth-button-icon-color);
    }
  }
}
